import * as XLSX from 'xlsx';

// Cell descriptor — lets callers emit a real numeric value (`v`) with an Excel
// number format (`z`) so spreadsheet users can SUM/AVG directly while still
// seeing the formatted string (e.g. "$1,234.56").
export interface CellDescriptor {
  v: number | string;
  z?: string;
  t?: 'n' | 's';
}

export type ExportCell =
  | string
  | number
  | null
  | undefined
  | CellDescriptor;

interface ExportConfig {
  fileName: string;
  sheetName: string;
  headers: string[];
  rows: ExportCell[][];
}

const isDescriptor = (c: ExportCell): c is CellDescriptor =>
  c != null && typeof c === 'object' && 'v' in c;

export function exportTableToExcel({
  fileName,
  sheetName,
  headers,
  rows,
}: ExportConfig): void {
  // aoa_to_sheet expects primitives; unwrap descriptors to their raw value.
  const plainRows = rows.map((row) =>
    row.map((cell) => (isDescriptor(cell) ? cell.v : cell))
  );
  const data: (string | number | null | undefined)[][] = [
    headers,
    ...plainRows,
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);

  // Apply per-cell formats (type + number format string) from descriptors.
  // Header row is at r=0, so data rows start at r=1.
  rows.forEach((row, rIdx) => {
    row.forEach((cell, cIdx) => {
      if (!isDescriptor(cell)) return;
      const addr = XLSX.utils.encode_cell({ r: rIdx + 1, c: cIdx });
      const wsCell = ws[addr];
      if (!wsCell) return;
      if (cell.t) wsCell.t = cell.t;
      if (cell.z) wsCell.z = cell.z;
    });
  });

  // Auto-fit column widths based on content.
  ws['!cols'] = headers.map((header, colIdx) => {
    let maxWidth = header.length;
    rows.forEach((row) => {
      const cell = row[colIdx];
      const raw = isDescriptor(cell) ? cell.v : cell;
      const len = raw != null ? String(raw).length : 0;
      if (len > maxWidth) maxWidth = len;
    });
    return { wch: Math.min(maxWidth + 2, 30) };
  });

  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileName}.xlsx`);
}
